
import { supabase } from "@/integrations/supabase/client";
import { Profile, UserRole } from "@/types/user";

/**
 * Checks if a user has admin privileges
 */
export async function checkIfUserIsAdmin(userId: string): Promise<boolean> {
  try {
    console.log("Checking admin status for user:", userId);
    
    // Check user metadata and profile table 
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) {
      return false;
    }
    
    // Check if the email is in the special_admin_emails table
    const email = userData.user.email?.toLowerCase();
    if (email) {
      // Use the database function instead of hardcoded checks
      const { data: isSpecialAdmin } = await supabase.rpc('is_special_admin_email', {
        check_email: email
      });
      
      if (isSpecialAdmin) {
        console.log("Special admin email detected:", email);
        return true;
      }
    }
    
    // Check user metadata
    if (userData?.user?.user_metadata?.isAdmin === true || userData?.user?.user_metadata?.role === "admin") {
      console.log("Admin role found in user metadata");
      return true;
    }
    
    // Check profile table
    const { data, error } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', userId)
      .single();
    
    if (error) {
      console.error("Error checking admin status in profiles:", error);
      return false;
    }
    
    const isAdmin = data?.role === 'admin';
    console.log("Admin status from profiles check:", isAdmin);
    return isAdmin;
  } catch (error) {
    console.error("Error checking admin status:", error);
    return false;
  }
}

/**
 * Gets user profile information
 */
export async function getUserProfile(userId: string): Promise<Profile | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
      
    if (error) {
      console.error("Error fetching user profile:", error);
      return null;
    }
    
    // Ensure the role is typed correctly
    return {
      ...data,
      role: data.role as UserRole
    };
  } catch (error) {
    console.error("Error in getUserProfile:", error);
    return null;
  }
}

/**
 * Updates a user's profile information
 */
export async function updateUserProfile(
  userId: string, 
  profileData: Partial<Omit<Profile, 'id' | 'created_at'>>
): Promise<{ success: boolean; message: string }> {
  try {
    const { error } = await supabase
      .from('profiles')
      .update(profileData)
      .eq('id', userId);
      
    if (error) throw error;
    
    return {
      success: true,
      message: "Profile updated successfully"
    };
  } catch (error: any) {
    console.error("Error updating profile:", error);
    return {
      success: false,
      message: error.message || "Failed to update profile"
    };
  }
}

/**
 * Gets all user profiles with optional filtering
 */
export async function getAllUserProfiles(filters?: {
  role?: UserRole;
  searchTerm?: string;
}): Promise<Profile[]> {
  try {
    let query = supabase.from('profiles').select('*');
    
    if (filters?.role) {
      query = query.eq('role', filters.role);
    }
    
    if (filters?.searchTerm) {
      query = query.or(`first_name.ilike.%${filters.searchTerm}%,last_name.ilike.%${filters.searchTerm}%,email.ilike.%${filters.searchTerm}%`);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error("Error fetching user profiles:", error);
      return [];
    }
    
    // Ensure all roles are typed correctly
    return (data || []).map(profile => ({
      ...profile,
      role: profile.role as UserRole
    }));
  } catch (error) {
    console.error("Error in getAllUserProfiles:", error);
    return [];
  }
}
